/**
 * Locale chunks, fetched on demand.
 *
 * English is bundled with the app because it is the fallback and the first
 * paint. Every other language is its own chunk, so a visitor downloads the one
 * language they read rather than all of them. At fifteen languages bundling
 * everything cost about two and a half megabytes of JSON; the list is more than
 * twice that now.
 *
 * This module deliberately does NOT import the configured i18n instance. It
 * takes whichever instance the caller already has, so a component or a hook can
 * load a language without pulling the whole i18n bootstrap into its module
 * graph.
 */

/** The slice of i18next this loader needs, so callers can pass a stub in tests. */
export interface ResourceStore {
  hasResourceBundle?: (lng: string, ns: string) => boolean;
  addResourceBundle?: (
    lng: string,
    ns: string,
    resources: unknown,
    deep?: boolean,
    overwrite?: boolean,
  ) => unknown;
}

const localeLoaders = import.meta.glob<{ default: Record<string, unknown> }>('./locales/*.json');

const inFlight = new Map<string, Promise<boolean>>();

/** `en-GB` and `pt-BR` resolve to the files we actually ship. */
export function baseLanguage(language: string | undefined): string {
  return (language || 'en').split('-')[0] ?? 'en';
}

/**
 * Resolve once the language's strings are available on `store`.
 *
 * Safe to call repeatedly: English is already bundled, a bundle that has been
 * added is not fetched again, and concurrent calls for the same language share
 * one request. Resolves to false when the strings could not be loaded (after
 * one retry): the caller then keeps the language on screen rather than
 * switching to one with no strings.
 */
export function loadLocaleInto(store: ResourceStore, language: string): Promise<boolean> {
  const lng = baseLanguage(language);
  if (lng === 'en') return Promise.resolve(true);
  if (typeof store.hasResourceBundle === 'function' && store.hasResourceBundle(lng, 'translation')) {
    return Promise.resolve(true);
  }
  if (typeof store.addResourceBundle !== 'function') return Promise.resolve(false);

  const existing = inFlight.get(lng);
  if (existing) return existing;

  const loader = localeLoaders[`./locales/${lng}.json`];
  if (!loader) return Promise.resolve(false);

  // One retry: a chunk that failed once (a flaky connection, a deploy in
  // progress) usually loads the second time.
  const task = loader()
    .catch(() => loader())
    .then((mod) => {
      store.addResourceBundle?.(lng, 'translation', mod.default, true, true);
      return true;
    })
    .catch(() => false)
    .finally(() => {
      inFlight.delete(lng);
    });

  inFlight.set(lng, task);
  return task;
}
